import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';

// Mock data for now - later we'll integrate with Nostr DMs
const mockChats = [
  {
    id: '1',
    contactId: 'alice',
    contactName: 'Alice García',
    contactPubkey: 'npub1alice123...', // Mock pubkey
    lastMessage: '¡Hola! ¿Qué tal?',
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 30), // 30 min ago
    unreadCount: 2,
    isOnline: true,
    isGroup: false,
  },
  {
    id: '2',
    contactId: 'bob',
    contactName: 'Bob Martínez',
    contactPubkey: 'npub1bob456...', // Mock pubkey
    lastMessage: 'Te mando el archivo que pediste',
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    unreadCount: 0,
    isOnline: false,
    isGroup: false,
  },
  {
    id: '3',
    contactId: 'carla',
    contactName: 'Carla Rodríguez',
    contactPubkey: 'npub1carla789...', // Mock pubkey
    lastMessage: '¿Vamos al cine mañana?',
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    unreadCount: 1,
    isOnline: true,
    isGroup: false,
  },
];

export function useChats() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['mensag-chats'],
    queryFn: async () => {
      // For now, return mock data
      // TODO: Query real NIP-17 encrypted messages
      await new Promise(resolve => setTimeout(resolve, 500));

      // In the future, this should:
      // 1. Query NIP-1039 (Gift Wrap) events from user's inbox relays
      // 2. Decrypt the seals to get the original messages
      // 3. Group them by conversation
      // 4. Get contact info from nicknames

      return mockChats;
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
}

export function useFindUser(nickname: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['mensag-find-user', nickname],
    queryFn: async () => {
      if (!nickname.trim()) return null;

      // Find user by nickname
      // Query kind:0 profiles with matching name/display_name
      const events = await nostr.query([
        {
          kinds: [0],
          '#t': ['mensag'], // Optional tag to identify MensaG users
          limit: 50
        }
      ], { signal: AbortSignal.timeout(5000) });

      // Find the user with matching nickname
      for (const event of events) {
        try {
          const profile = JSON.parse(event.content);
          if (profile.name === nickname.trim() ||
              profile.display_name === nickname.trim()) {
            return {
              pubkey: event.pubkey,
              profile,
              relays: event.tags.filter(tag => tag[0] === 'relay').map(tag => tag[1])
            };
          }
        } catch (e) {
          continue;
        }
      }

      return null;
    },
    enabled: !!nickname.trim(),
    staleTime: 60000, // Cache user lookups for 1 minute
  });
}

export function useChat(contactId: string) {
  const { data: chats, isLoading } = useChats();

  const chat = chats?.find(c => c.contactId === contactId);

  return {
    chat,
    isLoading,
  };
}

export function useMessages(contactId: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['mensag-messages', contactId],
    queryFn: async () => {
      // Mock implementation for basic functionality
      // TODO: Implement real NIP-17 message decryption
      await new Promise(resolve => setTimeout(resolve, 300));

      // Mock messages for different contacts
      const mockMessages: Record<string, any[]> = {
        alice: [
          {
            id: 'msg1',
            content: '¡Hola! ¿Cómo estás?',
            senderId: 'alice',
            senderName: 'Alice García',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
            isMine: false,
            status: 'read',
          },
          {
            id: 'msg2',
            content: '¡Hola Alice! Muy bien, gracias. ¿Y tú?',
            senderId: 'me',
            senderName: 'Yo',
            timestamp: new Date(Date.now() - 1000 * 60 * 45),
            isMine: true,
            status: 'read',
          },
          {
            id: 'msg3',
            content: '¡Genial! Estaba pensando en salir esta noche',
            senderId: 'alice',
            senderName: 'Alice García',
            timestamp: new Date(Date.now() - 1000 * 60 * 30),
            isMine: false,
            status: 'read',
          },
          {
            id: 'msg4',
            content: '¡Claro! ¿Dónde quedamos?',
            senderId: 'me',
            senderName: 'Yo',
            timestamp: new Date(Date.now() - 1000 * 60 * 15),
            isMine: true,
            status: 'delivered',
          },
        ],
        bob: [
          {
            id: 'msg1',
            content: 'Hola, te mando el archivo que pediste',
            senderId: 'bob',
            senderName: 'Bob Martínez',
            timestamp: new Date(Date.now() - 1000 * 60 * 120),
            isMine: false,
            status: 'read',
          },
        ],
        carla: [
          {
            id: 'msg1',
            content: '¿Vamos al cine mañana?',
            senderId: 'carla',
            senderName: 'Carla Rodríguez',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
            isMine: false,
            status: 'read',
          },
        ],
      };

      return mockMessages[contactId] || [];
    },
    staleTime: 15000, // Messages relatively fresh
  });
}